import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { User, LogOut, Menu } from 'lucide-react';
import { selectIsAuthenticated, selectUser, logout } from '../features/auth/authSlice';
import {
  fetchNotifications,
  selectUnreadCount,
  selectNotificationsStatus,
} from '../features/notifications/notificationsSlice';
import NotificationBell from './NotificationBell';
import CreditBalancePill from './CreditBalancePill';
import './MyCleanStudioHeader.css';

function MyCleanStudioHeader({ showMobileMenu = false, onMobileMenuClick = null }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectUser);
  const unreadCount = useSelector(selectUnreadCount);
  const notificationsStatus = useSelector(selectNotificationsStatus);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  
  // On home page, always show Articles link instead of user badge
  const isHomePage = location.pathname === '/';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  useEffect(() => {
    if (isAuthenticated && notificationsStatus === 'idle') {
      dispatch(fetchNotifications());
    }
  }, [isAuthenticated, notificationsStatus, dispatch]);

  const handleLogin = () => {
    navigate('/login');
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
    setShowDropdown(false);
  };

  const handleProfile = () => {
    navigate('/dashboard/profile');
    setShowDropdown(false);
  };

  const handleDashboard = () => {
    navigate('/dashboard');
  };

  // Get user's first name for display
  const getUserFirstName = () => {
    if (!user?.name) return 'User';
    return user.name.split(' ')[0];
  };

  return (
    <header className="mycleanstudio-header">
      <div className="mycleanstudio-header-content">
      {showMobileMenu && onMobileMenuClick && (
          <button
            className="mycleanstudio-mobile-menu-btn"
            onClick={onMobileMenuClick}
            aria-label="Toggle navigation menu"
          >
            <Menu size={20} />
            {isAuthenticated && unreadCount > 0 && (
              <span className="mycleanstudio-mobile-menu-dot" aria-hidden="true" />
            )}
          </button>
        )}
        <Link to="/" className="mycleanstudio-header-logo">
          <img src="/logo.png" alt="myCleanStudio" className="mycleanstudio-logo-img" />
          <h1 className="mycleanstudio-brand-name">myCleanStudio</h1>
        </Link>
        <nav className="mycleanstudio-header-nav">
          {isAuthenticated && !isHomePage ? (
            <ul className="mycleanstudio-nav-list">
              <li className="mycleanstudio-nav-item mycleanstudio-credit-nav-item">
                <CreditBalancePill />
              </li>
              <li className="mycleanstudio-nav-item mycleanstudio-notification-nav-item">
                <NotificationBell />
              </li>
              <li className="mycleanstudio-nav-item">
                <div className="mycleanstudio-user-dropdown" ref={dropdownRef}>
                  <button 
                    className="mycleanstudio-user-badge"
                    onClick={() => setShowDropdown(!showDropdown)}
                  >
                    <div className="mycleanstudio-user-badge-icon">
                      <User size={16} />
                    </div>
                    <span className="mycleanstudio-user-badge-name">{getUserFirstName()}</span>
                  </button>
                  {showDropdown && (
                    <ul className="mycleanstudio-dropdown-menu">
                      <li>
                        <button className="mycleanstudio-dropdown-item" onClick={handleProfile}>
                          <User size={16} />
                          Profile
                        </button>
                      </li>
                      <li>
                        <button className="mycleanstudio-dropdown-item" onClick={handleLogout}>
                          <LogOut size={16} />
                          Logout
                        </button>
                      </li>
                    </ul>
                  )}
                </div>
              </li>
            </ul>
          ) : (
            <ul className="mycleanstudio-nav-list">
              <li className="mycleanstudio-nav-item">
                <Link to="/articles" className="mycleanstudio-nav-link">Articles</Link>
              </li>
              {isAuthenticated ? (
                <li className="mycleanstudio-nav-item">
                  <button className="mycleanstudio-dashboard-btn" onClick={handleDashboard}>
                    My Dashboard
                  </button>
                </li>
              ) : (
                <li className="mycleanstudio-nav-item">
                  <button className="mycleanstudio-login-btn" onClick={handleLogin}>
                    Login
                  </button>
                </li>
              )}
            </ul>
          )}
        </nav>
      </div>
    </header>
  );
}

export default MyCleanStudioHeader;
